import { useEffect, useRef, useState } from "react";
import { post, put } from "@common/api/apiShared";
import { Result } from "@common/api/result";
import { useDotLegalMutation, useDotLegalQuery, useDotLegalQueryClient, useDotLegalQueryWithKey } from "@common/hooks/dotLegalReactQuery";
import { useDotLegalSnackbar } from "@dotlegal/dotlegal-ui-components";
import { useTranslation } from "@localization/useTranslation";
import { clampRailWidth } from "../../main/rail/Rail.hooks";
import { useAiChatState } from "../../aiChat/AiChatContext";
import DocumentPane from "../../main/panes/documentPane/DocumentPane";
import { publishStageDocumentOverlay } from "../../main/panes/documentPane/stageDocumentOverlay";
import { usePanes } from "../../main/panes/usePanes";
import { ContractStandardField, EntityTemplate, EntityTemplateField, getStandardFieldTranslationKey } from "../../entityTemplates/EntityTemplate.types";
import { ValidationError } from "@common/validationError";
import { isAutoRenewalShown } from "../contract/fields/AutoRenewalDurationField.utils";
import { ContractModel } from "../contract/Contract.types";
import { convertToUpdateModel, getContractQueryKey } from "../contract/Contract.hooks";
import { ContractExtractionResponse } from "../addContractButton/createContractPane/CreateContractPane.types";
import { isProposableParty } from "../addContractButton/createContractPane/extractionAdapter";
import { groupFieldsByTemplateStep } from "../../entityTemplates/templateStepFields/groupFieldsByTemplateStep";
import { useSelectableLegalEntityQuery } from "../../legalEntity/useSelectableLegalEntityQuery";
import { PreviewableDocument } from "@common/components/documentPreview/DocumentPreview.types";
import { useContractDocumentOptions } from "../contract/contractDocument/useContractDocumentOptions";
import { applyExtractionToContract, cloneContract } from "./applyExtractionToContract";
import { buildCitationLookup } from "./citationLookup";
import { getReviewableStandardFields, getReviewableTemplateFields, getReviewedCustomFieldValues } from "./reviewableTemplateFields";
import { isSameFieldValue, setFieldValue } from "./contractFieldValue";
import { getFieldValue, validateContractFields } from "../contract/ContractValidator.hooks";
import { usePermissionsProvider } from "../../auth/usePermissionsProvider";
import { useUserContext } from "../../auth/userContextProvider/UserContextProvider";
import { ContractAiUpdateStep, ContractPartyRequest, ReviewParty, UpdateContractFromExtractionRequest } from "./ContractAiUpdate.types";

/** Two columns of inputs need roughly this much before date pickers and amount fields start wrapping. */
const reviewRailWidth = 760;

interface ReviewState {
    contract: ContractModel;
    tagIds: Array<string>;
    parties: Array<ReviewParty>;
}

export interface UseContractAiUpdatePaneProps {
    contractId: string;
    documentId: string | undefined;
}

export function useContractAiUpdatePane(props: UseContractAiUpdatePaneProps) {
    const { translateString } = useTranslation();
    const snackbar = useDotLegalSnackbar();
    const queryClient = useDotLegalQueryClient();
    const { hasTagsAccess } = usePermissionsProvider();
    const { permissions } = useUserContext();
    const { widthState } = useAiChatState();
    const { open, closeSlot, refetchPageBehindPanes } = usePanes();

    const [selectedContractDocumentId, setSelectedContractDocumentId] = useState<string | undefined>(undefined);
    const [review, setReview] = useState<ReviewState | undefined>(undefined);
    // The AI's answer as it first arrived, never edited. Reverting a field has to be reversible, so the
    // proposal has to outlive the user overwriting it with the contract's current value.
    const [suggestion, setSuggestion] = useState<ReviewState | undefined>(undefined);
    const [activeQuote, setActiveQuote] = useState<string | undefined>(undefined);
    const [scannedDocumentId, setScannedDocumentId] = useState<string | undefined>(props.documentId);
    const [showValidationErrors, setShowValidationErrors] = useState(false);

    const contractQueryKey = getContractQueryKey(props.contractId);
    const { data: currentContract } = useDotLegalQueryWithKey<ContractModel>(contractQueryKey, `/contracts/${props.contractId}`);
    const { data: template } = useDotLegalQuery<EntityTemplate>(`/entity-templates/${currentContract?.entityTemplateId}`, { enabled: !!currentContract });
    const tagRelationUrl = `/tagRelation/${props.contractId}`;
    const { data: currentTagIds } = useDotLegalQuery<Array<string>>(tagRelationUrl);
    const { data: selectableLegalEntities, isLoading: isLoadingLegalEntities } = useSelectableLegalEntityQuery();
    const documentPicker = useContractDocumentOptions(props.contractId, selectedContractDocumentId);

    // The default rail fits one column; two plus the gutter leaves a date picker about 200px, which it
    // cannot use. Only ever widens, so a user who has already dragged it wider keeps their width.
    useEffect(() => {
        widthState.setWidth(clampRailWidth(Math.max(widthState.width, reviewRailWidth), window.innerWidth, widthState.minWidth));
        // Once, on open: re-running on every width change would fight the user's own drag.
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);

    const reviewableFields = getReviewableTemplateFields(template, {
        canAccessTags: hasTagsAccess,
    });

    const suggestMutation = useDotLegalMutation<Result<ContractExtractionResponse>, string>((documentId: string) =>
        post<ContractExtractionResponse>(`/contracts/${props.contractId}/documents/${documentId}/suggest-field-values`, {})
    );

    const saveMutation = useDotLegalMutation<Result<unknown>, UpdateContractFromExtractionRequest>((request: UpdateContractFromExtractionRequest) =>
        put(`/contracts/${props.contractId}/from-extraction`, request)
    );

    function runSuggestion(documentId: string) {
        setScannedDocumentId(documentId);
        suggestMutation.mutateAsync(documentId, {
            onSuccess: (response) => {
                const built = buildReviewState(
                    currentContract!,
                    currentTagIds ?? [],
                    response.value(),
                    getReviewableStandardFields(reviewableFields),
                    permissions.canCreateLegalEntitiesInline
                );
                setReview(built);
                setSuggestion(built);
            },
        });
    }

    /**
     * Puts the document on the stage before the read starts, so it can be read while the AI is working and
     * so the citations have something to point at afterwards. Only for the pane's own picker: opened from a
     * document, the caller has already put it there.
     */
    function startSuggestion(document: PreviewableDocument) {
        open(DocumentPane, { documents: [document], initialDocumentId: document.documentVersionId });
        runSuggestion(document.documentId);
    }

    // Opened from a document, the picker has already been answered elsewhere, so the scan starts itself.
    useEffect(() => {
        if (props.documentId && currentContract && !review && !suggestMutation.isLoading) {
            runSuggestion(props.documentId);
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [props.documentId, currentContract]);

    // What the document on the stage should highlight, republished only when it changes.
    useEffect(() => {
        if (scannedDocumentId) {
            publishStageDocumentOverlay({ documentId: scannedDocumentId, citationChunkContent: activeQuote });
        }
    }, [scannedDocumentId, activeQuote]);

    useEffect(() => () => publishStageDocumentOverlay(undefined), []);

    const fieldGroups = groupFieldsByTemplateStep(template, reviewableFields);

    const citationsByField = buildCitationLookup(suggestMutation.data?.success() ? suggestMutation.data.value() : undefined);

    const step: ContractAiUpdateStep = review ? "review" : suggestMutation.isLoading ? "analysing" : "pickDocument";

    function updateReview(change: (state: ReviewState) => void) {
        setReview((previous) => {
            if (!previous) {
                return previous;
            }

            const next: ReviewState = { contract: cloneContract(previous.contract), tagIds: [...previous.tagIds], parties: [...previous.parties] };
            change(next);
            return next;
        });
    }

    /**
     * What the row's toggle should offer. A field the AI proposed no change for has nothing to toggle; one
     * it did keeps its control even after the user reverts, which is the only way back to the finding.
     */
    function getFieldToggleState(field: EntityTemplateField) {
        if (!review || !suggestion || !currentContract) {
            return { canToggle: false, isShowingCurrentValue: false };
        }

        const current = getFieldValue(field, currentContract, currentTagIds ?? []);
        const suggested = getFieldValue(field, suggestion.contract, suggestion.tagIds);
        const proposed = getFieldValue(field, review.contract, review.tagIds);

        return {
            canToggle: !isSameFieldValue(current, suggested),
            isShowingCurrentValue: isSameFieldValue(current, proposed),
        };
    }

    function toggleField(field: EntityTemplateField) {
        if (!currentContract || !suggestion) {
            return;
        }

        const { isShowingCurrentValue } = getFieldToggleState(field);
        const value = isShowingCurrentValue
            ? getFieldValue(field, suggestion.contract, suggestion.tagIds)
            : getFieldValue(field, currentContract, currentTagIds ?? []);

        updateReview((state) => {
            // Tags are not on the contract, so the target carries them separately and the review has to
            // take back whatever setFieldValue put there.
            const target = { contract: state.contract, selectedTagIds: state.tagIds };
            setFieldValue(field, target, value);
            state.tagIds = target.selectedTagIds;
        });
    }

    const endDateField = reviewableFields.find((field) => field.standardFieldIdentifier === ContractStandardField.EndDate);

    /**
     * The AI never proposes a renewal period, so unlike a field's arrow this one cannot lead back to a finding:
     * it appears once the user has changed the period, and only ever takes it back to the contract's. A
     * contract without an end date shows no period to take back to, so it offers no arrow either.
     */
    const canRevertAutoRenewal =
        !!review &&
        !!currentContract &&
        !!endDateField &&
        isAutoRenewalShown(endDateField, currentContract.endDate) &&
        (!isSameFieldValue(currentContract.autoRenewalDuration, review.contract.autoRenewalDuration) ||
            !isSameFieldValue(currentContract.autoRenewalDurationType, review.contract.autoRenewalDurationType));

    function revertAutoRenewal() {
        updateReview((state) => {
            state.contract.autoRenewalDuration = currentContract!.autoRenewalDuration;
            state.contract.autoRenewalDurationType = currentContract!.autoRenewalDurationType;
        });
    }

    /**
     * Only the fields the review shows: the save sends nothing else, so an invalid field the user cannot see
     * would stay as it is whatever happens here, and blocking on it would block them on nothing they can fix.
     * Checked only once Save has been tried, as the create pane does: flagging every field the AI did not
     * find before the user has touched anything would paint the whole proposal red.
     */
    const validationErrors: Array<ValidationError> =
        review && showValidationErrors
            ? [
                  ...validateContractFields(reviewableFields, review.contract, review.tagIds, hasTagsAccess, translateString),
                  ...validateReviewParties(review.parties),
              ]
            : [];

    // The review keeps its own party list, so the edit page's rule does not apply as it stands. Only "none"
    // is an error: an included party without a legal entity is one the user has chosen to create.
    function validateReviewParties(parties: Array<ReviewParty>): Array<ValidationError> {
        const counterPartiesField = reviewableFields.find((field) => field.standardFieldIdentifier === ContractStandardField.CounterParties);
        if (!counterPartiesField?.required || parties.some((party) => party.included)) {
            return [];
        }

        return [new ValidationError(getStandardFieldTranslationKey(ContractStandardField.CounterParties), translateString("requiredField"))];
    }

    const counterPartiesErrorText = validationErrors.find(
        (error) => error.field === getStandardFieldTranslationKey(ContractStandardField.CounterParties)
    )?.error;

    const reviewBodyRef = useRef<HTMLDivElement>(null);

    // MUI marks every errored input with the class, so a new kind of error is found without changes here; the
    // party list is not an input and marks its own.
    function scrollToFirstError() {
        reviewBodyRef.current?.querySelector(".Mui-error, [data-validation-error]")?.scrollIntoView({ behavior: "smooth", block: "center" });
    }

    /** Whether the review may be saved. The first call is what turns the errors on. */
    function trySave(): boolean {
        if (!review) {
            return false;
        }

        setShowValidationErrors(true);
        const errors = [
            ...validateContractFields(reviewableFields, review.contract, review.tagIds, hasTagsAccess, translateString),
            ...validateReviewParties(review.parties),
        ];
        if (errors.length > 0) {
            // The errors are not in the DOM until the state above has rendered.
            requestAnimationFrame(scrollToFirstError);
            return false;
        }

        return true;
    }

    function save() {
        if (!review || validationErrors.length > 0) {
            return;
        }

        const request: UpdateContractFromExtractionRequest = {
            contract: convertToUpdateModel(review.contract),
            customFieldValues: getReviewedCustomFieldValues(review.contract.customFieldValues, reviewableFields).map((value) => ({
                customInputFieldId: value.customInputFieldId,
                value: value.value,
            })),
            tagIds: review.tagIds,
            contractParties: review.parties.filter((party) => party.included).map(toPartyRequest),
        };

        saveMutation.mutateAsync(request, {
            onSuccess: () => {
                snackbar.show(translateString("contractUpdated"), "success");
                queryClient.refetchQueries(contractQueryKey);
                queryClient.refetchQueries(tagRelationUrl);
                refetchPageBehindPanes();
                closeSlot("right");
            },
        });
    }

    // The contract's own parties in the shape the review list renders, so one component draws both columns.
    // Both columns are sorted by name: in the AI's order the same two parties could read as different lists.
    const currentParties: Array<ReviewParty> = sortByName(
        (currentContract?.counterParties ?? []).map((party) => ({
            key: party.id,
            legalEntityId: party.legalEntityId,
            name: selectableLegalEntities?.find((entity) => entity.id === party.legalEntityId)?.name ?? translateString("unknown"),
            proposedNew: false,
            included: true,
        }))
    );
    const proposedParties = sortByName(review?.parties ?? []);

    /**
     * What a current party's control should offer, asked the way the field rows ask it: a party the AI
     * proposed as well leaves nothing to decide, so only the ones it dropped get a control at all.
     */
    function getPartyToggleState(party: ReviewParty) {
        if (!party.legalEntityId || !suggestion) {
            return { canToggle: false, isShowingCurrentValue: false };
        }

        const holds = (parties: Array<ReviewParty>) => parties.some((candidate) => candidate.legalEntityId === party.legalEntityId);

        return { canToggle: !holds(suggestion.parties), isShowingCurrentValue: holds(review?.parties ?? []) };
    }

    function toggleCurrentParty(party: ReviewParty) {
        updateReview((state) => {
            state.parties = getPartyToggleState(party).isShowingCurrentValue
                ? state.parties.filter((candidate) => candidate.legalEntityId !== party.legalEntityId)
                : [...state.parties, { ...party, included: true }];
        });
    }

    const partiesToBeRemoved = (currentContract?.counterParties ?? [])
        .filter((party) => party.legalEntityId)
        .filter((party) => !(review?.parties ?? []).some((reviewParty) => reviewParty.included && reviewParty.legalEntityId === party.legalEntityId))
        .map((party) => selectableLegalEntities?.find((entity) => entity.id === party.legalEntityId)?.name ?? translateString("unknown"));

    return {
        step,
        currentContract,
        currentParties,
        proposedParties,
        currentTagIds: currentTagIds ?? [],
        review,
        fieldGroups,
        citationsByField,
        documentPicker,
        selectedContractDocumentId,
        setSelectedContractDocumentId,
        selectableLegalEntities,
        isLoadingLegalEntities,
        isSaving: saveMutation.isLoading,
        partiesToBeRemoved,
        getFieldToggleState,
        toggleField,
        canRevertAutoRenewal,
        revertAutoRenewal,
        validationErrors,
        counterPartiesErrorText,
        reviewBodyRef,
        trySave,
        getPartyToggleState,
        toggleCurrentParty,
        startSuggestion,
        save,
        onShowInDocument: setActiveQuote,
        onProposedChange: async (changeFunc: (contract: ContractModel) => void) => updateReview((state) => changeFunc(state.contract)),
        onProposedTagsChange: (tagIds: Array<string>) => updateReview((state) => (state.tagIds = tagIds)),
        onRemoveParty: (key: string) => updateReview((state) => (state.parties = state.parties.filter((party) => party.key !== key))),
        onToggleParty: (key: string) =>
            updateReview((state) => (state.parties = state.parties.map((party) => (party.key === key ? { ...party, included: !party.included } : party)))),
        onAddParty: (legalEntityId: string) =>
            updateReview((state) => {
                const entity = selectableLegalEntities?.find((candidate) => candidate.id === legalEntityId);
                state.parties = [
                    ...state.parties,
                    { key: legalEntityId, legalEntityId, name: entity?.name ?? translateString("unknown"), proposedNew: false, included: true },
                ];
            }),
    };
}

/**
 * The proposal the user starts from: the contract with the AI's findings written over it, and a party list
 * that is the AI's findings alone. Parties are replace rather than merge, so keeping an existing party is
 * something the user does deliberately; a party we could not match starts unticked, because ticking it
 * creates a legal entity, and is left out for a user who may not create one.
 */
function buildReviewState(
    currentContract: ContractModel,
    currentTagIds: Array<string>,
    extraction: ContractExtractionResponse,
    reviewableStandardFields: Set<ContractStandardField>,
    canCreateLegalEntities: boolean
): ReviewState {
    const proposableParties = extraction.contractParties.filter((party) => isProposableParty(party, canCreateLegalEntities));

    return {
        contract: applyExtractionToContract(currentContract, extraction, reviewableStandardFields),
        tagIds: [...currentTagIds],
        parties: proposableParties.map((party, index) => ({
            key: party.matchedLegalEntityId ?? `proposed-${index}`,
            legalEntityId: party.matchedLegalEntityId,
            name: party.extractedName,
            address: party.extractedAddress,
            city: party.extractedCity,
            zipCode: party.extractedZipCode,
            country: party.extractedCountry,
            registrationNumber: party.extractedRegistrationNumber,
            proposedNew: !party.matchedLegalEntityId,
            included: !!party.matchedLegalEntityId,
            citation: party.citation,
        })),
    };
}

function sortByName(parties: Array<ReviewParty>): Array<ReviewParty> {
    return [...parties].sort((left, right) => left.name.localeCompare(right.name));
}

function toPartyRequest(party: ReviewParty): ContractPartyRequest {
    if (party.legalEntityId) {
        return { legalEntityId: party.legalEntityId };
    }

    return {
        extractedName: party.name,
        extractedAddress: party.address,
        extractedCity: party.city,
        extractedZipCode: party.zipCode,
        extractedCountry: party.country,
        extractedRegistrationNumber: party.registrationNumber,
    };
}
